import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { X } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Overlays are built on Radix rather than hand-rolled.
 *
 * Focus trapping, focus restoration, escape handling, scroll locking, aria wiring and
 * inert backgrounds are each individually easy to get wrong and collectively the
 * difference between a dialog that works with a screen reader and one that traps a
 * keyboard user with no way out. The Approve dialog is where a chamber applies its legal
 * authority — it is not the place to be improvising a focus trap.
 */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  }
>(function DialogContent({ className, children, size = 'md', ...props }, ref) {
  const SIZES = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[min(1600px,95vw)]',
  } as const;
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-[var(--pv-z-overlay)] bg-[var(--pv-overlay)] backdrop-blur-[1px]',
          'data-[state=open]:animate-[pv-fade_var(--pv-duration)_var(--pv-ease)]',
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-[var(--pv-z-dialog)] flex max-h-[92vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
          'rounded-[var(--pv-radius-xl)] border border-[var(--pv-line)] bg-[var(--pv-card)] shadow-[var(--pv-shadow-dialog)]',
          'data-[state=open]:animate-[pv-rise_var(--pv-duration)_var(--pv-ease-out)]',
          SIZES[size],
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export function DialogHeader({
  title,
  description,
  eyebrow,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-start gap-4 border-b border-[var(--pv-line)] px-6 py-4',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {eyebrow && <div className="pv-eyebrow mb-1">{eyebrow}</div>}
        <DialogPrimitive.Title className="[font-size:var(--pv-text-h2)] font-bold">
          {title}
        </DialogPrimitive.Title>
        {description && (
          <DialogPrimitive.Description className="mt-1.5 [font-size:var(--pv-text-sm)] leading-relaxed text-[var(--pv-muted)]">
            {description}
          </DialogPrimitive.Description>
        )}
      </div>
      <DialogPrimitive.Close
        className="-mr-1 -mt-1 rounded-[var(--pv-radius-sm)] p-1.5 text-[var(--pv-muted)] transition-colors hover:bg-[rgba(20,48,30,0.06)] hover:text-[var(--pv-ink)]"
        aria-label="Close"
      >
        <X aria-hidden className="size-4" />
      </DialogPrimitive.Close>
    </div>
  );
}

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)}>{children}</div>;
}

export function DialogFooter({
  children,
  className,
  note,
}: {
  children: ReactNode;
  className?: string;
  /** Left-aligned context — what happens when the primary action is pressed. */
  note?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-3 border-t border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-6 py-3.5',
        className,
      )}
    >
      {note && (
        <p className="min-w-0 flex-1 [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-muted)]">
          {note}
        </p>
      )}
      <div className={cn('flex items-center gap-2', !note && 'ml-auto')}>{children}</div>
    </div>
  );
}

/* ── Tooltip ──────────────────────────────────────────────────────────────── */

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  children,
  content,
  side = 'top',
}: {
  children: ReactNode;
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-[var(--pv-z-dropdown)] max-w-[32ch] rounded-[var(--pv-radius-sm)] bg-[var(--pv-ink)] px-2.5 py-1.5 [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-on-ink)] shadow-[var(--pv-shadow)]"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-[var(--pv-ink)]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ── Dropdown menu ────────────────────────────────────────────────────────── */

export const Menu = DropdownPrimitive.Root;
export const MenuTrigger = DropdownPrimitive.Trigger;

export function MenuContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-[var(--pv-z-dropdown)] min-w-48 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card)] p-1 shadow-[var(--pv-shadow-lg)]',
          'data-[state=open]:animate-[pv-rise_var(--pv-duration-fast)_var(--pv-ease-out)]',
          className,
        )}
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}

export function MenuItem({
  children,
  onSelect,
  destructive,
  icon,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  destructive?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <DropdownPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-[var(--pv-radius-sm)] px-2.5 py-1.5 [font-size:var(--pv-text-sm)] outline-none',
        'data-[highlighted]:bg-[var(--pv-paper-sunk)] data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
        destructive
          ? 'text-[var(--pv-stop)] data-[highlighted]:bg-[var(--pv-stop-bg)]'
          : 'text-[var(--pv-ink-soft)] data-[highlighted]:text-[var(--pv-ink)]',
      )}
    >
      {icon}
      {children}
    </DropdownPrimitive.Item>
  );
}

export function MenuSeparator() {
  return <DropdownPrimitive.Separator className="my-1 h-px bg-[var(--pv-line)]" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownPrimitive.Label className="px-2.5 pb-1 pt-2 [font-size:var(--pv-text-xs)] font-semibold  text-[var(--pv-faint)]">
      {children}
    </DropdownPrimitive.Label>
  );
}

/* ── Tabs ─────────────────────────────────────────────────────────────────── */

export const Tabs = TabsPrimitive.Root;

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <TabsPrimitive.List
      className={cn('flex items-center gap-1 border-b border-[var(--pv-line)]', className)}
    >
      {children}
    </TabsPrimitive.List>
  );
}

export function TabsTrigger({
  value,
  children,
  count,
}: {
  value: string;
  children: ReactNode;
  count?: number;
}) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      className={cn(
        'relative -mb-px flex items-center gap-2 border-b-2 border-transparent px-3 py-2 [font-size:var(--pv-text-sm)] font-medium text-[var(--pv-muted)]',
        'transition-colors hover:text-[var(--pv-ink)]',
        // The active tab is a selected state, so it follows the chamber's brand.
        'data-[state=active]:border-[var(--pv-brand)] data-[state=active]:text-[var(--pv-ink)] data-[state=active]:font-semibold',
      )}
    >
      {children}
      {count !== undefined && (
        <span className="pv-num rounded-[var(--pv-radius-pill)] bg-[var(--pv-card-alt)] px-1.5 [font-size:var(--pv-text-2xs)] font-semibold">
          {count}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <TabsPrimitive.Content value={value} className={cn('pv-animate-fade outline-none', className)}>
      {children}
    </TabsPrimitive.Content>
  );
}
